import {Component, OnInit, HostListener} from '@angular/core';
import {QuickConfigService} from './api/quick-config.service';
import {QuickConfigData} from './domain/QuickConfigData';
import {PostMessage} from './util/post-message';
import {UrlUtil} from './util/url-util';
import urlJoin from 'proper-url-join';
import {UserData} from './domain/UserData';
import {Angulartics2GoogleAnalytics} from 'angulartics2/ga';
import {RecieveMessage} from './util/recieve-message';
import {LocalStorageService} from 'angular-2-local-storage';
import {LabelService} from './label/label.service';
import {HttpParams, HttpUrlEncodingCodec} from '@angular/common/http';
import {SubscribeActionProcessor} from './util/subscribe-action-processor';

@Component({
  selector: 'cfg-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {
  
  configLoaded = false;
  initError = false;
  pendingMessages: any[] = [];
  quickConfigData: QuickConfigData;
  userData: UserData;

  constructor(private initService: QuickConfigService,
              private postMessage: PostMessage,
              private recieveMessage: RecieveMessage,
              private angulartics2: Angulartics2GoogleAnalytics,
              private localStorage: LocalStorageService,
              private labelService: LabelService) {
  }
 @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
   const allowedOrigin = UrlUtil.getIFEDomain();
     try {
      const eventUrl = new URL(event.origin);
      const allowedUrl = new URL(allowedOrigin);
      if (eventUrl.protocol !== allowedUrl.protocol || eventUrl.hostname !== allowedUrl.hostname) {
        console.warn('Received message from unauthorized origin:', event.origin);
        return;
      }
    } catch {
      console.warn('Received message from invalid origin:', event.origin);
      return;
    }

    const data = event.data;
     if (!this.configLoaded) {      
      this.pendingMessages.push(event.data);
      return;
    }

    // Handle price update message
    if (data && data.type === 'PRICE_UPDATE') {      
      this.handlePriceUpdate(data.payload);
    }
  }




  ngOnInit() {
    this.loadConfig();
  }

  loadConfig() {
    this.initError = false;
    this.initService.setupSessionCookieAcrossDomains().subscribe(
      () => {
        this.initService.init().subscribe(
          quickConfigInitData => {
            this.labelService.initialize(quickConfigInitData.labels);
            this.setQuickConfigInitData(quickConfigInitData);
            this.publishMessages(quickConfigInitData);
            if (quickConfigInitData.hasKnowledgeBaseChanged && !this.localStorage.get(quickConfigInitData.itemId)) {
              let knowledgeBaseChangedMessage;
              knowledgeBaseChangedMessage = quickConfigInitData.labels['knowledgebaseChanged'] + '\n\n'
                + quickConfigInitData.labels['knowledgebaseChanged2'];
              alert(knowledgeBaseChangedMessage);
              this.localStorage.add(quickConfigInitData['itemId'], 15);
            }
            this.warnIfProductNotConfigurable(quickConfigInitData);

            this.configLoaded = true;

            this.pendingMessages.forEach(message => {
              if (message && message.type === 'PRICE_UPDATE') {              
              // Payload should have headerData and items array
              this.handlePriceUpdate(message.payload);
            }
           
          });

          this.pendingMessages = [];

          },
          error => this.handleInitError('error during init', error)
        );
      },
      error => this.handleInitError('error during session cookie setup', error)
    );
  }

  /**
   * Handles a failure while initializing the config. Surfaces the error and
   * leaves the component in a state that allows a retry so queued PRICE_UPDATE
   * messages are not silently dropped forever.
   */
  private handleInitError(context: string, error: any) {
    console.error(context, error);
    this.initError = true;
    this.postMessage.publishErrorMessage(error);
  }

  retryLoadConfig() {
    this.loadConfig();
  }

  getInitErrorMessage(): string {
    return this.labelService.get('initError') || 'Unable to load configuration. Please try again.';
  }

  getRetryLabel(): string {
    return this.labelService.get('retry') || 'Retry';
  }


  /**
   * FHS-4515: the IFE rejects the save of a product it rates incompatible; tell the user on load
   * instead of only at save. Options stay available.
   */
  warnIfProductNotConfigurable(quickConfigInitData: QuickConfigData) {
    if (quickConfigInitData.isProductDataIssue()) {
      alert(quickConfigInitData.labels['productDataIssue']
        || 'This product cannot be configured because of a product data issue. Please contact your administrator.');
    } else if (!quickConfigInitData.isProductConfigurable()) {
      alert(quickConfigInitData.labels['productNotConfigurable']
        || 'The selected product cannot be configured with the current selections, so this item cannot be saved.'
        + ' You can still review the options.');
    }
  }

  private publishMessages(quickConfigInitData) {
    this.postMessage.publishPriceMessage(quickConfigInitData.price);
    this.postMessage.publishLeadTimeMessage(quickConfigInitData.leadTimeTotal);
    this.postMessage.publishLoadedMessage();
    this.postMessage.displaySpecialOptionButton(
      quickConfigInitData.categories.reduce((result, category) => result.concat(category.parentSubcategories), []));
  }

  setQuickConfigInitData(initData: QuickConfigData) {
    this.quickConfigData = initData;
    this.userData = initData.userData;
    this.recieveMessage.updateQuickConfigData(this.quickConfigData);
  }

  getCssPath(): string {
    const cssUrlParam = UrlUtil.getCssPath();
    if (cssUrlParam != null) {
      return cssUrlParam;
    }
    const orgFolderHomeQueryParam = UrlUtil.getOrgFolderHome();
    const orgFolderHome = orgFolderHomeQueryParam == null ? 'acme' : orgFolderHomeQueryParam;
    return UrlUtil.getOrgUnitFolderHome() ?
      urlJoin(UrlUtil.getIFEDomain(), 'config', orgFolderHome, UrlUtil.getOrgUnitFolderHome(), 'css') :
      urlJoin(UrlUtil.getIFEDomain(), 'config', orgFolderHome, 'css');
  }

  trackByCategory(index, category) {
    return category ? category.id : undefined;
  }

    /**
   * Handles incoming price updates from parent frame and applies them immediately.
   * Payload format: {headerData, items[]}
   */
  private handlePriceUpdate(payload: any) {
    if (!payload || !payload.items || !Array.isArray(payload.items) || payload.items.length === 0) {
      console.warn('Invalid price update payload:', payload);
      // Ack even here: the datasheet has no other way to learn the message went nowhere.
      this.postMessage.publishErpPricesAppliedMessage(0, 0);
      return;
    }

    const headerData = payload.headerData || {};
    // Single agreed contract with the Workato/Franklin payload: the currency
    // ISO code is sent as `headerData.priceCurrencyCode`. Warn (instead of silently
    // defaulting) if it is missing so a broken contract is visible.
    let currency = headerData.priceCurrencyCode;
    if (!currency) {
      console.warn('Price update payload missing headerData.priceCurrencyCode; defaulting to USD', headerData);
      currency = 'USD';
    }

    const items = payload.items
      .filter(item => !!item.OptionId || !!item.ObjectID)
      .map(item => ({
        optionId: item.OptionId,
        objectID: item.ObjectID,
        price: Number(item.ListPrice),
        currency: currency,
        quantity: Number(item.Quantity),
        multiplier: Number(item.Modifier) || 1
      }))
      .filter(item => {
        const label = item.optionId || item.objectID;
        if (!Number.isFinite(item.price)) {
          console.warn(`Skipping item ${label}: invalid price`, item.price);
          return false;
        }
        if (!Number.isFinite(item.quantity)) {
          console.warn(`Skipping item ${label}: invalid quantity`, item.quantity);
          return false;
        }
        return true;
      });

    if (items.length === 0) {
      console.warn('No valid items in price update payload');
      this.postMessage.publishErpPricesAppliedMessage(0, payload.items.length);
      return;
    }

    const groups = new Map<string, { items: any[], optionData: any }>();

    for (const item of items) {
      // Match on OptionId (the CKB option's stable string ID) before falling back to
      // ObjectID. ObjectID is assigned per configurator instantiation, so the datasheet's
      // copy and this panel's copy disagree whenever the item was saved against an older
      // published Knowledge Base — measured on betanext 2026-08-05: the datasheet sent
      // 22970/21412/19978 for an item saved 2026-08-03 while the panel held
      // 22979/21417/19983 for the same three options. OptionId matched exactly in both.
      // Re-pricing existing quote items is this feature's primary use case: FHS-4645's
      // scope question "Do we need to manually update pricing for existing quotes, or will
      // we require users to enter the configuration and initiate the 'Get Pricing'
      // function?" is answered "Force users to re-price." (that line is under Scope
      // Questions, not the Basic Acceptance Criteria). So ObjectID alone silently prices
      // nothing on exactly the items this feature exists for.
      const optionData = this.findOptionInCategories(item.optionId)
        || this.findOptionInCategories(item.objectID);
      if (optionData) {
        item.optionData = optionData;
        const key = optionData.parentSubcatId;
        let group = groups.get(key);
        if (!group) {
          group = { items: [], optionData };
          groups.set(key, group);
        }
        group.items.push(item);
      } else {
        console.warn(`Option not found in configuration (OptionId=${item.optionId}, ObjectID=${item.objectID})`);
      }
    }

    // Acknowledge back to the datasheet once every group has settled, so it can tell
    // "posted" from "applied" — it gates Full Config's skip-the-ERP-fetch flag on this.
    // The applied count comes from the submit callbacks rather than from the loop above,
    // because a group whose submitFieldChange fails was resolved but never applied.
    const totalItems = payload.items.length;

    if (groups.size === 0) {
      // Every lookup missed. This is the stale-ObjectID case that made Full Config show
      // stale prices: the message arrived and parsed, and nothing was priced.
      this.postMessage.publishErpPricesAppliedMessage(0, totalItems);
      return;
    }

    let pendingGroups = groups.size;
    let appliedCount = 0;

    groups.forEach(({ items: groupItems, optionData }) => {
      this.submitPriceOverrides(groupItems, currency, optionData, (applied: boolean) => {
        if (applied) {
          appliedCount += groupItems.length;
        }
        if (--pendingGroups === 0) {
          this.postMessage.publishErpPricesAppliedMessage(appliedCount, totalItems - appliedCount);
        }
      });
    });
  }

  /**
   * Finds an option/choice in the categories hierarchy by its OptionId or ObjectID.
   * Returns: {choice, field, parentSubcatId, categoryId}
   */
  private findOptionInCategories(optionId: string): any {
    // Callers try OptionId then ObjectID, so either may be absent from the payload.
    // Returning early also keeps the "not initialized" warning to one per lookup pair.
    if (!optionId) {
      return null;
    }
    if (!this.quickConfigData || !this.quickConfigData.categories) {
      console.warn('QuickConfigData not initialized yet');
      return null;
    }
    for (const category of this.quickConfigData.categories) {
      for (const parentSubcategory of category.parentSubcategories) {
        for (const subcategory of parentSubcategory.subcategories) {
          for (const field of subcategory.fields) {
            if (field.choices) {
              const choice = field.choices.find(c => c.id === optionId || c.httpValue === optionId);
              if (choice) {
                return {
                  choice: choice,
                  field: field,
                  subcategoryId: subcategory.id,
                  parentSubcatId: parentSubcategory.id,
                  categoryId: category.id,
                  fieldId: field.id
                };
              }
            }
          }
        }
      }
    }
    return null;
  }

  /**
   * Submits price overrides to backend via HTTP
   * Parameters format matches option-override.component.ts pattern:
   * basePriceOverride_{suffix}, priceCalcMethod_{suffix}, ovrSourceCurrency_{suffix},
   * where suffix is the resolved choice's httpValue, falling back to its id, and finally
   * to the payload's ObjectID. That last hop is unreachable from handlePriceUpdate (an
   * item only gets here once optionData is set) and is kept only so a future caller that
   * skips the lookup degrades to the old behaviour instead of posting "_undefined".
   *
   * onSettled, when supplied, is invoked exactly once with whether the submit succeeded.
   * handlePriceUpdate uses it to tell the datasheet what was actually applied.
   */
  private submitPriceOverrides(items: any[], currency: string, firstOptionData: any,
                               onSettled?: (applied: boolean) => void) {
    let urlParams = new HttpParams({encoder: new HttpUrlEncodingCodec()});

    // Build HTTP params for all price updates
    items.forEach(item => {
      // Derive the suffix from the option we actually resolved, not from the payload's
      // ObjectID: on an item saved against an older published KB the payload's ObjectID is
      // stale (see handlePriceUpdate), and posting basePriceOverride_<stale id> targets an
      // option the server no longer knows. `choice.httpValue || choice.id` is also exactly
      // what option-override.component.ts:91 uses, so this now matches that pattern rather
      // than only claiming to.
      const choice = item.optionData ? item.optionData.choice : null;
      const paramSuffix = (choice && (choice.httpValue || choice.id)) || item.objectID;

      // Set the base price override
      urlParams = urlParams.set('basePriceOverride_' + paramSuffix, item.price.toString());
      
      // Set price calculation method to "EnterPrice" to indicate manual override
      urlParams = urlParams.set('priceCalcMethod_' + paramSuffix, 'EnterPrice');
      
      // Set the currency code for the override
      urlParams = urlParams.set('ovrSourceCurrency_' + paramSuffix, currency);

      // Set the sell multiplier if provided
      if (item.multiplier && item.multiplier !== 1) {
        urlParams = urlParams.set('sellMultiplier_' + paramSuffix, item.multiplier.toString());
      }

      // Update the choice object directly for immediate UI reflection,
      // reusing the option already located in handlePriceUpdate.
      if (item.optionData) {
        item.optionData.choice.sellMultiplier = item.multiplier;
      }
    });

    // Submit to backend - use the parent subcategory of the first option found
    this.initService.submitFieldChange(firstOptionData.parentSubcatId, urlParams)
      .subscribe(
        (response) => {
          // SubscribeActionProcessor handles updating UI and publishing new price
          new SubscribeActionProcessor(this.postMessage).processOptionChange(response, this.quickConfigData);
          // Price overrides applied successfully
          if (onSettled) { onSettled(true); }
        },
        (error) => {
          console.error('Error submitting price override:', error);
          if (onSettled) { onSettled(false); }
        }
      );
  }

}
